import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  BookOpen, 
  Award, 
  Clock, 
  CheckCircle2,
  PlayCircle,
  ArrowRight,
  TrendingUp,
  Target
} from "lucide-react";

const MyProgress = () => {
  const { user } = useAuth();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/my-progress`, {
        withCredentials: true
      });
      setProgressData(response.data);
    } catch (error) {
      toast.error("Failed to load progress data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  const stats = [
    {
      label: "Total Courses",
      value: progressData?.total_courses || 0,
      icon: BookOpen,
      color: "text-blue-600",
      bg: "bg-blue-50"
    },
    {
      label: "Completed",
      value: progressData?.completed_courses || 0,
      icon: CheckCircle2,
      color: "text-green-600",
      bg: "bg-green-50"
    },
    {
      label: "In Progress",
      value: progressData?.in_progress_courses || 0,
      icon: PlayCircle,
      color: "text-orange-600",
      bg: "bg-orange-50"
    },
    {
      label: "Certificates",
      value: progressData?.total_certificates || 0,
      icon: Award,
      color: "text-violet-600",
      bg: "bg-violet-50"
    }
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="my-progress-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
            My Progress
          </h1>
          <p className="text-stone-600 mt-2">Track your learning journey</p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${stat.bg}`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{stat.value}</p>
                    <p className="text-sm text-stone-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Overall Completion Rate */}
        {progressData?.total_courses > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-violet-50 rounded-lg">
                    <Target className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-stone-900">Overall Completion Rate</h3>
                    <p className="text-sm text-stone-500">Your learning progress across all courses</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-violet-700">
                  {progressData?.overall_completion_rate || 0}%
                </span>
              </div>
              <Progress 
                value={progressData?.overall_completion_rate || 0} 
                className="h-3"
              />
            </CardContent>
          </Card>
        )}

        {/* Course Progress List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Course Progress
            </h2>
            <Link to="/courses">
              <Button variant="outline" size="sm">
                Browse Courses
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>

          {progressData?.courses?.length > 0 ? (
            <div className="grid gap-4">
              {progressData.courses.map((course) => (
                <Card key={course.course_id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <div className="flex">
                    {/* Thumbnail */}
                    <div className="hidden sm:block w-48 h-32 bg-stone-100 flex-shrink-0">
                      {course.thumbnail ? (
                        <img 
                          src={course.thumbnail} 
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="h-8 w-8 text-stone-300" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-stone-900 mb-1">{course.title}</h3>
                          <div className="flex items-center gap-2 text-sm text-stone-500">
                            <Clock className="h-4 w-4" />
                            Enrolled {new Date(course.enrolled_at).toLocaleDateString()}
                          </div>
                        </div>
                        {course.is_completed ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Completed
                          </Badge>
                        ) : course.progress > 0 ? (
                          <Badge className="bg-orange-100 text-orange-700">
                            In Progress
                          </Badge>
                        ) : (
                          <Badge className="bg-stone-100 text-stone-600">
                            Not Started
                          </Badge>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-stone-600">
                            {course.materials_completed} of {course.materials_total} materials completed
                          </span>
                          <span className="font-medium text-violet-700">{course.progress}%</span>
                        </div>
                        <Progress value={course.progress} className="h-2" />
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3 mt-4">
                        <Link to={`/courses/${course.course_id}`}>
                          <Button size="sm" className="bg-violet-700 hover:bg-violet-800">
                            {course.progress > 0 ? 'Continue Learning' : 'Start Course'}
                          </Button>
                        </Link>
                        {course.certificate_id && (
                          <Link to={`/certificate/${course.certificate_id}`}>
                            <Button size="sm" variant="outline">
                              <Award className="h-4 w-4 mr-2" />
                              View Certificate
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-stone-900 mb-2">No Courses Yet</h3>
                <p className="text-stone-600 mb-6">
                  You haven't enrolled in any courses yet. Start your learning journey today!
                </p>
                <Link to="/courses">
                  <Button className="bg-violet-700 hover:bg-violet-800">
                    Browse Courses
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MyProgress;
